import { useEffect, useMemo, useRef } from 'react';

import { Trans } from '@lingui/react/macro';
import { LinearProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';

import {
  convertLatestImageToImageObject,
  formatMixPanelData,
} from '@/modules/annotator/casePresentation/patientDownloadable/hooks/usePatientPrintoutData';
import { PatientPrintout } from '@/modules/annotator/casePresentation/patientDownloadable/PatientPrintout';
import { useVisitSummaries } from '@/react-query/useVisitSummaries';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { DataSource } from '../../../@types/global';
import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import { analysesToLatestImage } from '../../../react-query/useLatestImagesv2';
import type { GetPublicPatientDownloadableDataResponse } from '../../../react-query/usePublicPatientDownloadable';
import { usePublicPatientDownloadable } from '../../../react-query/usePublicPatientDownloadable';
import { blueBlack630 } from '../../../theme/videa-style-tokens';
import { getExternalJwtToken } from '../../../utils/api';
import { useCornerstoneInitialization } from '../../../utils/cornerstone/cornerstoneAtoms';
import { decodeJwtToken } from '../../../utils/jwt';
import { LoadingErrorContainer } from '../ExternalUrlHandler';

import { PatientDownloadableStateProvider } from './PatientDownloadableStateProvider';

export const PatientDownloadableHandler = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  // Get JWT token from localStorage
  const token = getExternalJwtToken();

  // Extract patient and practice IDs from JWT token
  const decoded = decodeJwtToken(token || '');
  const patientId = decoded?.patientId;
  const practiceId = decoded?.practiceId;

  // Get remaining URL parameters
  const shortCode = urlParams.get('shortCode');

  const {
    data: patientData,
    isLoading,
    isError,
    error,
  } = usePublicPatientDownloadable({
    shortCode: shortCode || '',
  });

  // Initialize cornerstone
  useCornerstoneInitialization();

  if (!token) {
    return (
      <LoadingErrorContainer>
        <div className="message-container">
          <VideaTypography variant="headingMedium" color={blueBlack630}>
            <Trans>JWT token is required. Please check your link.</Trans>
          </VideaTypography>
        </div>
      </LoadingErrorContainer>
    );
  }

  if (!patientId || !practiceId) {
    return (
      <LoadingErrorContainer>
        <div className="message-container">
          <VideaTypography variant="headingMedium" color={blueBlack630}>
            <Trans>
              Invalid JWT token. Patient or practice information not found.
            </Trans>
          </VideaTypography>
        </div>
      </LoadingErrorContainer>
    );
  }

  if (isLoading) {
    return (
      <LoadingErrorContainer>
        <div className="message-container">
          <VideaTypography variant="headingMedium" color={blueBlack630}>
            <Trans>Loading your dental analysis...</Trans>
          </VideaTypography>
          <LinearProgress />
        </div>
      </LoadingErrorContainer>
    );
  }

  if (
    isError ||
    !patientData ||
    !patientData.atoms ||
    !patientData.visitIdToImageIds
  ) {
    const isExpiredError =
      error instanceof Error && error.message.includes('expired');

    return (
      <LoadingErrorContainer>
        <div className="message-container">
          <VideaTypography variant="headingMedium" color={blueBlack630}>
            {isExpiredError ? (
              <Trans>
                This link has expired. Please contact your dental practice for a
                new link.
              </Trans>
            ) : (
              <Trans>
                Unable to load your dental data. Please try again later.
              </Trans>
            )}
          </VideaTypography>
        </div>
      </LoadingErrorContainer>
    );
  }

  return (
    <PatientDownloadableStateProvider
      atomsFromQRCode={patientData.atoms}
      rblTemplateName={patientData.rblTemplateName}
      practiceFeatureFlags={patientData.practiceFeatureFlags}
      practiceCountry={patientData.practiceCountry}
      patientInfo={patientData.patientInfo}
    >
      <PatientDownloadableContent
        patientData={patientData}
        patientId={patientId}
      />
    </PatientDownloadableStateProvider>
  );
};

const PatientDownloadableContent = ({
  patientData,
  patientId,
}: {
  patientData: GetPublicPatientDownloadableDataResponse;
  patientId: string;
}) => {
  const {
    latestImages,
    visitIdToImageIds,
    isIframe,
    externalPatientId,
    externalImageId,
    reverseTrialData,
  } = patientData;

  const { trackEvent } = useTrackEvent();
  const hasTrackedEvent = useRef(false);

  const [visitSummaries] = useVisitSummaries({
    visits: visitIdToImageIds.map((visit) => ({
      estimatedVisitId: visit.estimatedVisitId,
      unifiedAppointmentId: visit.unifiedAppointmentId,
    })),
  });

  const visitIdToImagesMap = useMemo(() => {
    // Convert V2 format to V1 format using the same function as useLatestImagesV2
    const convertedLatestImages = latestImages.map((latestImage) =>
      analysesToLatestImage(latestImage),
    );

    // Process the converted images to add the necessary properties expected by PatientDownloadable
    // This follows the same pattern as useLatestImagesV2ToV1's select function
    const processedLatestImages = convertedLatestImages.flatMap(
      (latestImage) => {
        return latestImage.analyzedImages.map((image: any) => {
          // Ensure we have valid image paths
          // In the V2 API, imageKey might be the actual key needed for image loading
          const imageKey = image.imageKey || '';
          const imagePath = image.path || image.originalPath || imageKey || '';
          const originalPath =
            image.originalPath || image.path || imageKey || '';
          const sharpenedPath =
            image.sharpenedPath || image.path || imageKey || '';

          const processedImage = {
            ...image,
            // the old workflow has a few complex functions needing this, and the PPD workflow does not
            // Add this dummy prop for now until the old workflow is removed
            path: imagePath,
            originalPath: originalPath,
            sharpenedPath: sharpenedPath,
            patientInfo: {
              patientId: patientId,
              externalPatientId: null,
            },
            // detect uses objectId as a image id in the old VCA workflow.
            // Instead of changing that in multiple places just set the prop here
            objectId: image.imageId,
            dataSource: DataSource.NUCLEUS,
            // Ensure imageKeys has the proper structure expected by getSelectedImagePath
            imageKeys: {
              original: { key: originalPath, version: '1' },
              s3path: imagePath,
              sharpened: { key: sharpenedPath, version: '1' },
              sharpest: { key: sharpenedPath, version: '1' },
              enhanced: { key: imagePath, version: '1' },
              generalized: { key: imagePath, version: '1' },
            },
            // Ensure these properties are set for cornerstone compatibility
            fileName: image.fileName || `image_${image.imageId}`,
            fileDateCreated: image.createdAt || new Date().toISOString(),
            imageMetadata: image.imageMetadata || {
              anatomicRegionSequenceName: 'DENTAL',
            },
            rotation: image.rotation || 0,
            imageIndex: image.imageIndex || 0,
            videaErrorCode: image.videaErrorCode || null,
            videaError: image.videaError || null,
            // In the normal latest-images flow, createUserAnnotation adds a
            // source property, which is used in computeVisibleFindings to
            // determine whether the finding is drawn
            userAnnotations: image.userAnnotations?.map((annotation: any) => ({
              ...annotation,
              source: 'userAnnotations',
            })),
          };

          const visitForImage = visitIdToImageIds.find((visit) =>
            visit.imageIds.includes(image.imageId),
          );

          return convertLatestImageToImageObject(
            processedImage,
            visitForImage?.estimatedVisitId,
          );
        });
      },
    );

    return visitIdToImageIds.map((visit) => ({
      estimatedVisitId: visit.estimatedVisitId,
      unifiedAppointmentId: visit.unifiedAppointmentId,
      images: processedLatestImages.filter((image) =>
        visit.imageIds.includes(image.imageId),
      ),
    }));
  }, [latestImages, patientId, visitIdToImageIds]);

  useEffect(() => {
    if (
      !patientData?.patientInfo ||
      !visitSummaries.data ||
      hasTrackedEvent.current
    )
      return;
    trackEvent(mixpanelEvents.qrCodeScanned, {
      patientId: patientData.patientInfo.patientId,
      imageIds: patientData.visitIdToImageIds.flatMap(
        (visit) => visit.imageIds,
      ),
      ...formatMixPanelData(visitSummaries.data || []),
      isIframe,
      externalPatientId,
      externalImageId,
      trialStatus: reverseTrialData?.trialStatus ?? null,
      centralizedBilling: reverseTrialData?.centralizedBilling ?? null,
    });
    hasTrackedEvent.current = true;
  }, [
    isIframe,
    externalPatientId,
    externalImageId,
    patientData,
    trackEvent,
    visitSummaries.data,
    reverseTrialData,
  ]);

  // ! don't add patient name here, this is non-phi
  return (
    <div className="vai-w-full vai-h-full vai-flex vai-justify-center vai-items-center vai-bg-white vai-overflow-auto">
      <div className="vai-w-full vai-h-full vai-max-w-[30em] vai-bg-white vai-flex vai-justify-center vai-p-0">
        <PatientPrintout
          scale={1}
          isLoading={visitSummaries.isLoading}
          visitSummaries={visitSummaries.data ?? []}
          visitIdToImagesMap={visitIdToImagesMap}
        />
      </div>
    </div>
  );
};
